import { useCallback, useEffect, useRef, useState } from 'react'
import { SEARCH_DEBOUNCE_MS } from '@cannect/constants/products'
import api from '../../../../../../services/api'

export interface Product {
  id: number
  uuid: string
  name: string
  translated_name: string
  details: string
  price: number
  stock: number
  brand_id: number
  class_id: number
  type: string
  media: Array<{
    path: string
    format: string
  }>
  brand: {
    id: number
    name: string
  }
  shape: {
    id: number
    name: string
  }
  medicalData: {
    volume: number
    unit_of_measurement: string
  }
}

interface ProductsResponse {
  success: boolean
  products: {
    count: number
    rows: Product[]
    page: number
    limit: number
  }
}

export interface ShapeFilter {
  id: number | null
  name: string
}

export const SHAPE_FILTERS: ShapeFilter[] = [
  { id: null, name: 'Todos' },
  { id: 1, name: 'Óleo' },
  { id: 29, name: 'Flor Medicinal' }
]

interface UseProductsReturn {
  products: Product[]
  loading: boolean
  error: string | null
  page: number
  limit: number
  totalCount: number
  searchTerm: string
  setSearchTerm: (term: string) => void
  selectedShapeId: number | null
  setSelectedShapeId: (shapeId: number | null) => void
  fetchProducts: (page?: number, search?: string, shapeId?: number | null) => Promise<void>
}

export const useProducts = (): UseProductsReturn => {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [limit] = useState(24)
  const [totalCount, setTotalCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedShapeId, setSelectedShapeId] = useState<number | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isInitialMountRef = useRef(true)

  const fetchProducts = useCallback(
    async (pageNumber = 1, search = '', shapeId: number | null = null) => {
      setLoading(true)
      setError(null)

      try {
        const encodedSearch = encodeURIComponent(search)
        let url = `/list-products?limit=${limit}&translated_name=${encodedSearch}&delivery_by_association=true&association_site=true&page=${pageNumber}`

        if (shapeId !== null) {
          url += `&shape_id=${shapeId}`
        }

        const response = await api.get<ProductsResponse>(url)

        if (response.data.success) {
          setProducts(response.data.products.rows)
          setPage(response.data.products.page)
          setTotalCount(response.data.products.count)
        }
      } catch (err) {
        setError('Erro ao carregar produtos. Tente novamente.')
        console.error('Error fetching products:', err)
      } finally {
        setLoading(false)
      }
    },
    [limit]
  )

  // Filtro por clique (shape) busca na hora, sem debounce
  useEffect(() => {
    if (isInitialMountRef.current) return
    fetchProducts(1, searchTerm, selectedShapeId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShapeId])

  // Busca por digitação usa debounce para não disparar uma request a cada tecla
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      fetchProducts(1, searchTerm, selectedShapeId)
      return undefined
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      fetchProducts(1, searchTerm, selectedShapeId)
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  return {
    products,
    loading,
    error,
    page,
    limit,
    totalCount,
    searchTerm,
    setSearchTerm,
    selectedShapeId,
    setSelectedShapeId,
    fetchProducts
  }
}
